import { Environment, Network, RecordSource, Store } from 'relay-runtime';
import type { GraphQLResponse, PayloadError } from 'relay-runtime';
import { uncrunch } from 'graphql-crunch';
import type { CrunchedData } from 'graphql-crunch';
import type { PayloadExtensions } from 'relay-runtime/lib/network/RelayNetworkTypes';
import { useState } from 'react';
import { API_URL } from './services/url';
import { generateLoginUrl } from './services/auth';
import { formatSystemMessage } from './i18n/formatSystemMessage';

export class NotAuthenticatedError extends Error {}
export class ForbiddenError extends Error {}
export class SomethingWentWrongError extends Error {
    static default() {
        return new SomethingWentWrongError(
            // If you need to update this message or other formatSystemMessage, go to: i18n/systemMessages/en.ts and i18n/systemMessages/sv.ts
            formatSystemMessage('errors.forbidden'),
        );
    }
}

export const createRelayEnvironment = () => {
    const recordSource = new RecordSource();
    const store = new Store(recordSource, {
        // set the cache expiration time to 5 minutes.
        queryCacheExpirationTime: 5 * 60 * 1000,
    });
    const GRAPHQL_API = `${API_URL}/graphql/v2`;

    const network = Network.create(
        async (
            operation,
            variables,
            /*, cacheConfig, uploadables*/
        ) => {
            const crunchVersion =
                'true' === window.localStorage?.getItem('enableGraphQLCrunch')
                    ? 2
                    : 0;

            const response = await fetch(GRAPHQL_API, {
                // fetchTimeout: 20000, // Timeout after 20s.
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-GraphQLCrunch': crunchVersion.toString(),
                    'X-Binosight-RelayGlobalId': 'packed:v1', // off, standard, packed, packed:v1
                    'X-Binosight-Use-Versioned-Questionnaire-Ids': 'true',
                },
                body: JSON.stringify({
                    query: operation.text,
                    variables,
                }),
            });

            const handle401OrNotAuthenticated = () => {
                // Most query operations should redirect in the page error boundary, but as of now, mutations do not
                // manually redirect, and in the future this likely applies to subscriptions as well.
                if (operation.operationKind !== 'query') {
                    window.location.href = generateLoginUrl(window.location);
                }
                throw new NotAuthenticatedError(
                    formatSystemMessage('errors.notAuthenticated'),
                );
            };
            const handleForbidden = () => {
                throw new ForbiddenError(
                    formatSystemMessage('errors.forbidden'),
                );
            };

            if (response.status === 401) {
                handle401OrNotAuthenticated();
                return {
                    data: null,
                };
            }
            if (response.status === 403) {
                handleForbidden();
                return {
                    data: null,
                };
            }

            const contentType = response.headers.get('Content-Type') || '';
            if (!contentType.includes('application/json')) {
                throw SomethingWentWrongError.default();
            }

            const payload: GraphQLResponse = await response
                .json()
                .catch((error) => {
                    console.error('Failed to parse response as JSON:', error);
                    throw SomethingWentWrongError.default();
                })
                .then((payload) => {
                    if (!payload || typeof payload !== 'object') {
                        console.error(
                            'Server returned an unexpected response format',
                        );
                        throw SomethingWentWrongError.default();
                    }
                    if (
                        'data' in payload &&
                        payload.data?.crunched &&
                        payload.data.version === crunchVersion
                    ) {
                        return uncrunch(payload.data as CrunchedData);
                    }
                    return payload;
                });

            if ('errors' in payload && payload.errors) {
                console.error(payload.errors);

                const isAuthError = (
                    error: PayloadError | { extensions: PayloadExtensions },
                ) => {
                    if ('extensions' in error) {
                        return ['UNAUTHENTICATED', 'UNAUTHORIZED'].includes(
                            error.extensions.code,
                        );
                    }
                    const m = error.message.toLowerCase();
                    return (
                        m.includes('unauthorized') ||
                        m.includes('not authorized')
                    );
                };

                function isPermissionError(
                    error: PayloadError | { extensions: PayloadExtensions },
                ): boolean {
                    if ('extensions' in error) {
                        return error.extensions.code === 'FORBIDDEN';
                    }
                    const m = error.message.toLowerCase();
                    return (
                        m.includes('forbidden') ||
                        m.includes('permission denied')
                    );
                }

                const authError = payload.errors.find(isAuthError);
                if (authError) {
                    handle401OrNotAuthenticated();
                }

                const permissionError = payload.errors.find(isPermissionError);
                if (permissionError) {
                    handleForbidden();
                }

                if (
                    payload.errors.some((error) =>
                        error.message.includes('fetch failed'),
                    )
                ) {
                    throw new Error('Server error');
                }
                if (operation.operationKind === 'mutation') {
                    payload.data = null;
                } else {
                    throw payload.errors[0];
                }
            }

            return payload;
        },
    );
    return new Environment({
        network,
        store,
    });
};
let relayEnvironment = createRelayEnvironment();
const resetRelayEnviromentListeners: Array<() => void> = [];
export const getRelayEnvironment = () => relayEnvironment;
export const addResetRelayEnviromentListener = (listener: () => void) => {
    resetRelayEnviromentListeners.push(listener);
};

export const useRelayEnvironmentAndReset = (): [
    typeof relayEnvironment,
    () => void,
] => {
    const [environment, setEnvironment] = useState(relayEnvironment);

    return [
        environment,
        () => {
            relayEnvironment = createRelayEnvironment();
            setEnvironment(relayEnvironment);

            resetRelayEnviromentListeners.forEach((listener) => listener());
        },
    ];
};
